/**
 * Portal Results view (SPEC E4 R3, AC6 — KPI summary).
 *
 * Summary KPI cards for a CLIENT: total leads, meetings booked
 * (status/last_call_outcome == meeting_booked), and total pipeline value (from
 * the E3 summary, when available). Pulls from /api/prospects (count + outcome
 * breakdown) and /api/pipeline/summary (value) — no new analytics endpoint.
 *
 * This is a metrics summary; richer charts are a later additive enhancement.
 */

import { useQuery } from '@tanstack/react-query'
import {
  getPortalLeads,
  getPortalPipelineSummary,
  type PortalLead,
} from '@/lib/api/portal'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

function isMeetingBooked(lead: PortalLead): boolean {
  return (
    lead.status === 'meeting_booked' ||
    lead.last_call_outcome === 'meeting_booked'
  )
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <Card data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-3xl font-semibold">{value}</p>
      </CardContent>
    </Card>
  )
}

export function PortalResults() {
  const leadsQuery = useQuery({
    queryKey: ['portal', 'leads', 'results'],
    queryFn: () => getPortalLeads(500),
  })
  // Pipeline value is best-effort — a tenant with no opportunities still gets
  // the lead KPIs; the value card just shows $0 / "—".
  const summaryQuery = useQuery({
    queryKey: ['portal', 'pipeline', 'summary', 'results'],
    queryFn: () => getPortalPipelineSummary(),
    retry: false,
  })

  if (leadsQuery.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading results…</p>
  }
  if (leadsQuery.isError) {
    return (
      <p className="text-sm text-destructive">
        Could not load results. Please try again.
      </p>
    )
  }

  const leads = leadsQuery.data?.prospects ?? []
  const totalLeads = leadsQuery.data?.total ?? leads.length
  const meetingsBooked = leads.filter(isMeetingBooked).length
  const pipelineValue = summaryQuery.data?.total_value ?? 0

  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
      data-testid="portal-results"
    >
      <KpiCard label="Total Leads" value={String(totalLeads)} />
      <KpiCard label="Meetings Booked" value={String(meetingsBooked)} />
      <KpiCard
        label="Pipeline Value"
        value={pipelineValue ? `$${pipelineValue.toLocaleString()}` : '—'}
      />
    </div>
  )
}
